import { useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * Selector for the scroll container rendered by the shared `<Table>` (see
 * `packages/ui/src/table.tsx`). Tables built on `<Table>` can't attach a ref
 * to that container, so they attach the hook's ref to an ancestor and pass
 * this selector to resolve the scroller within it.
 */
export const TABLE_CONTAINER_SELECTOR = '[data-slot="table-container"]';

/**
 * Marker for a table scroll container the ROUTER must reset on navigation
 * (see `scrollToTopSelectors` in `app/router/router.tsx`). Tables with
 * modal-local pagination (no navigation) don't need it — the hook below is
 * their only reset.
 */
export const TABLE_SCROLL_TO_TOP_SELECTOR = '[data-scroll-to-top]';

/**
 * Resets a paginated table's vertical scroll to the top whenever `page` or
 * `pageSize` changes. Runs in a layout effect (before paint, so the new page
 * never flashes scrolled) and skips the initial mount.
 *
 * Attach the returned ref to the table's scroll container — or, for tables
 * built on the shared `<Table>`, to any ancestor plus
 * `TABLE_CONTAINER_SELECTOR` to resolve the container within it.
 *
 * This hook alone does NOT win on router-driven pages: TanStack Router's
 * scroll restoration snapshots every scrolled element and writes the saved
 * position back after navigation, after this effect runs. Router-driven
 * tables additionally carry `data-scroll-to-top` (see
 * `TABLE_SCROLL_TO_TOP_SELECTOR`), which the router itself scrolls to top.
 */
export function useTableScrollReset(
  page: number,
  pageSize: number,
  withinSelector?: string,
): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const root = ref.current;
    if (root === null) return;
    const scroller =
      withinSelector === undefined
        ? root
        : (root.querySelector<HTMLElement>(withinSelector) ?? root);
    scroller.scrollTop = 0;
  }, [page, pageSize, withinSelector]);

  return ref;
}
